import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import { withRouter } from 'react-router-dom';
import GridLoader from '@hobs/components/Grid/GridLoader';
import ColumnMenu from '@hobs/components/Grid/ColumnMenu';
import { H2 } from '@hobs/components/Headings';
import { Grid, GridColumn } from '@progress/kendo-react-grid';
import Panel from '@hobs/components/Panel';
import { createLoadingSelector } from '@hobs/utils/loading';
import { format, formatLocal } from '@hobs/utils/formatWithTimeZone';
import InputLabel from '@hobs/components/InputLabel';
import Icon from '@hobs/components/Icon';
import { GhostButton } from '@hobs/components/Buttons';
import {
  DATE_FORMAT,
  DATETIME_FORMAT,
  PAGE_SIZES
} from '@hobs/utils/constants';
import { getAuditLogItems } from '@hobs/actions/serviceRequestAuditLog';

import './ServiceRequestAuditLogItems.scss';
import Money from '@hobs/components/Money';
import Quantity from '@hobs/components/Quantity';

class ServiceRequestAuditLogItems extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      auditLog: props.auditLog,
      auditLogItems: [],
      auditLogItemDataState: {
        take: 10,
        skip: 0,
        sort: [{ field: 'FieldName', dir: 'asc' }]
      }
    };
  }

  getDisplayDate = date => {
    return formatLocal(date, DATETIME_FORMAT);
  };

  dataStateChange = e => {
    this.setState(
      {
        auditLogItemDataState: e.dataState
      },
      () => {
        this.loadODataQuery();
      }
    );
  };

  loadODataQuery = requestedServicesAuditLogId => {
    const { auditLog } = this.state;
    this.props
      .getAuditLogItems(
        `RequestedServicesAuditLogItemsQuery(${
          requestedServicesAuditLogId
            ? requestedServicesAuditLogId
            : auditLog.RequestedServicesAuditLogId
        })`,
        this.state.auditLogItemDataState
      )
      .then(response => {
        if (response.success) {
          this.setState({
            auditLogItems: response
          });
        } else {
          this.setState({
            auditLogItems: []
          });
        }
      });
  };

  componentDidMount() {
    this.loadODataQuery();
  }

  componentWillUnmount() {
    this.setState({
      auditLogItems: []
    });
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.auditLog.RequestedServicesAuditLogId !=
      this.props.auditLog.RequestedServicesAuditLogId
    ) {
      this.setState(
        {
          auditLog: this.props.auditLog,
          auditLogItemDataState: {
            ...this.state.auditLogItemDataState,
            skip: 0
          }
        },
        () => {
          this.loadODataQuery(this.props.auditLog.RequestedServicesAuditLogId);
        }
      );
    }
  }

  closeDetails = () => {
    this.setState({
      auditLog: null,
      auditLogItemDataState: {
        ...this.state.auditLogItemDataState,
        skip: 0
      },
      auditLogItems: []
    });
    this.props.onClose();
  };

  expand() {
    event.target.classList.toggle('expanded');
  }

  getFieldValue = (value, dateValue, fieldName) => {
    const namesOfMoneyFields = ['price', 'cost', 'gross'];
    const fieldsToExclude = ['requestnumber', 'servicecode'];
    const isNumber = dateValue
      ? false
      : !isNaN(Number.parseFloat(value)) &&
        !fieldsToExclude.some(x => fieldName.toLowerCase().includes(x));
    const isMoney =
      isNumber &&
      namesOfMoneyFields.some(x => fieldName.toLowerCase().includes(x));

    return (
      <td onClick={this.expand}>
        {dateValue ? (
          format(dateValue, DATE_FORMAT)
        ) : isMoney ? (
          <Money amount={value} />
        ) : isNumber ? (
          <Quantity amount={value} />
        ) : (
          value
        )}
      </td>
    );
  };

  render() {
    const { t, getActionTypeValue, getPhaseTypeValue } = this.props;
    const { auditLogItems, auditLog } = this.state;
    return (
      <>
        {auditLog && (
          <div className="auditLog-item">
            <>
              <Panel>
                <GhostButton
                  className="close-button"
                  iconLeft={<Icon icon="Close" />}
                  onClick={this.closeDetails}
                />
                <div className="auditLog-item--details">
                  <H2>{t('Details')}</H2>
                  <div className="auditLog-item--details--row">
                    <div className="auditLog-item--details--column">
                      <InputLabel>{t('Date')}</InputLabel>
                      {this.getDisplayDate(auditLog.CreatedDate)}
                    </div>
                    <div className="auditLog-item--details--column">
                      <InputLabel>{t('User')}</InputLabel>
                      {auditLog.UserFullName}
                    </div>
                  </div>
                  <div className="auditLog-item--details--row">
                    <div className="auditLog-item--details--column">
                      <InputLabel>{t('Phase')}</InputLabel>
                      {getPhaseTypeValue(auditLog.PhaseTypeId)}
                    </div>
                    <div className="auditLog-item--details--column">
                      <InputLabel>{t('Party')}</InputLabel>
                      {auditLog.Party.Name}
                    </div>
                  </div>
                  <div className="auditLog-item--details--row">
                    <div className="auditLog-item--details--column">
                      <InputLabel>{t('Action')}</InputLabel>
                      {getActionTypeValue(auditLog.ActionTypeId)}
                    </div>
                    <div className="auditLog-item--details--column">
                      <InputLabel>{t('Email')}</InputLabel>
                      {auditLog.Email}
                    </div>
                  </div>
                  <div className="auditLog-item--details--row">
                    <div className="auditLog-item--details--column">
                      <InputLabel>{t('Currency')}</InputLabel>
                      {auditLog.Currency || '-'}
                    </div>
                    <div className="auditLog-item--details--column">
                      <InputLabel>{t('Phone')}</InputLabel>
                      {auditLog.Phone || '-'}
                    </div>
                  </div>
                </div>
              </Panel>
              <Grid
                sortable={true}
                pageable={{
                  pageSizes: PAGE_SIZES
                }}
                {...this.state.auditLogItemDataState}
                {...auditLogItems}
                onDataStateChange={this.dataStateChange}
              >
                <GridColumn
                  field="FieldName"
                  title={t('Field name')}
                  columnMenu={ColumnMenu}
                />
                <GridColumn
                  field="OldValue"
                  title={t('Old')}
                  columnMenu={ColumnMenu}
                  cell={({ dataItem }) =>
                    this.getFieldValue(
                      dataItem.OldValue,
                      dataItem.OldDateTimeValue,
                      dataItem.FieldName
                    )
                  }
                />
                <GridColumn
                  field="NewValue"
                  title={t('New')}
                  columnMenu={ColumnMenu}
                  cell={({ dataItem }) =>
                    this.getFieldValue(
                      dataItem.NewValue,
                      dataItem.NewDateTimeValue,
                      dataItem.FieldName
                    )
                  }
                />
              </Grid>
              {this.props.isLoading && <GridLoader />}
            </>
          </div>
        )}
      </>
    );
  }
}

ServiceRequestAuditLogItems.propTypes = {
  isLoading: PropTypes.bool,
  match: PropTypes.object,
  t: PropTypes.func,
  auditLog: PropTypes.object,
  getAuditLogItems: PropTypes.func,
  onClose: PropTypes.func,
  gridFilterValues: PropTypes.func,
  getActionTypeValue: PropTypes.func,
  getPhaseTypeValue: PropTypes.func
};

const oDataLoading = createLoadingSelector(['OData']);
const mapStateToProps = state => ({
  isLoading: oDataLoading(state)
});

const mapDispatchToProps = {
  getAuditLogItems
};

export default withRouter(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )(withTranslation()(ServiceRequestAuditLogItems))
);
